import React, { useState } from 'react';
import { Dropdown, Select, Button, message, Tag } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getEmployeeByBranchId } from '@src/util/query/employee.query';
import { UpdateScheduleByField } from '@src/util/mutation/schedule.mutation';

// duty status codes used across the Schedule/Rota pages: 0 = open (unassigned),
// 1 = confirmed by employee, 2 = assigned to employee (awaiting confirmation), 3 = assigned to agency
const STATUS_LABEL = {
  '0': { text: 'Open', color: 'red' },
  '1': { text: 'Confirmed', color: 'green' },
  '2': { text: 'Assigned', color: 'blue' },
  '3': { text: 'Agency', color: 'purple' },
};

export const DutyStatusTag = ({ status }) => {
  const cfg = STATUS_LABEL[status] || { text: status || 'Unknown', color: 'default' };
  return <Tag color={cfg.color} style={{ marginRight: 0 }}>{cfg.text}</Tag>;
};

// quick-assign action shown on open (unassigned) duty rows inside a dashboard
// drawer. This is a fast path for the dashboard only - it does not run the
// skill/site-eligibility matching the full Schedule/Rota assignment screens do,
// so duties with specific training requirements should still be assigned there.
export const FulfillDutyAction = ({ row, onFulfilled }) => {
  const [visible, setVisible] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [getEmployees, { data: employeeData, loading: employeeLoading }] = useLazyQuery(getEmployeeByBranchId);
  const [updateScheduleByField] = useMutation(UpdateScheduleByField);

  const employees = (employeeData?.getEmployeeByBranchId?.response || []).filter(e => !e?.isArchived);

  const openPicker = () => {
    setVisible(true);
    if (row?.branchId) {
      getEmployees({ variables: { branchId: row.branchId }, fetchPolicy: 'network-only' });
    }
  };

  const handleFulfil = async () => {
    if (!selectedEmployeeId) return;
    setSubmitting(true);
    try {
      // three focused field updates rather than one bulk call - mirrors how the
      // Schedule/Rota pages assign an employee via updateScheduleByField, and
      // the isPublished write is what triggers the existing assignment
      // email/push notification to the employee
      await updateScheduleByField({ variables: { input: { _id: row._id, field: 'assignedEmployee', value: selectedEmployeeId } } });
      await updateScheduleByField({ variables: { input: { _id: row._id, field: 'status', value: '2' } } });
      await updateScheduleByField({ variables: { input: { _id: row._id, field: 'isPublished', value: true } } });

      const employee = employees.find(e => e?._id === selectedEmployeeId);
      onFulfilled?.(row._id, {
        status: '2',
        assignedEmployee: selectedEmployeeId,
        employeeName: employee ? `${employee.firstname || ''} ${employee.lastname || ''}`.trim() : undefined,
      });
      message.success('Duty assigned - the employee has been notified');
      setVisible(false);
      setSelectedEmployeeId(null);
    } catch (err) {
      message.error(err?.message || 'Could not assign this duty');
    } finally {
      setSubmitting(false);
    }
  };

  if (row?.status !== '0') {
    return <DutyStatusTag status={row?.status} />;
  }

  return (
    <Dropdown
      trigger={['click']}
      open={visible}
      onOpenChange={(v) => { setVisible(v); if (!v) setSelectedEmployeeId(null); }}
      dropdownRender={() => (
        <div style={{ width: 260, padding: 12, background: '#fff', borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,0.12)' }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Assign an employee</div>
          <Select
            style={{ width: '100%' }}
            placeholder="Search employee..."
            showSearch
            loading={employeeLoading}
            value={selectedEmployeeId}
            onChange={setSelectedEmployeeId}
            filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
            options={employees.map(e => ({ value: e._id, label: `${e.firstname || ''} ${e.lastname || ''}`.trim() }))}
            notFoundContent={employeeLoading ? 'Loading...' : 'No employees in this branch'}
          />
          <Button
            type="primary"
            size="small"
            block
            style={{ marginTop: 10 }}
            disabled={!selectedEmployeeId}
            loading={submitting}
            onClick={handleFulfil}
          >
            Confirm Assignment
          </Button>
        </div>
      )}
    >
      <Button size="small" type="primary" ghost onClick={openPicker}>Fulfil</Button>
    </Dropdown>
  );
};
